import { useState } from "react";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { 
  Settings, 
  Mail, 
  CheckCircle, 
  UserCheck, 
  LogOut,
  ChevronDown,
  ChevronUp,
  QrCode,
  Shield,
  Building2
} from "lucide-react";
import { useDemoMode } from "../lib/DemoModeContext";
import { useTesterAccess } from "../lib/hooks/useTesterAccess";
import { supabase } from "../lib/supabase";

interface DevModePanelProps {
  onNavigate: (page: string, state?: Record<string, unknown>) => void; // ✅ FIX 6: Replace 'any' with proper type
}

export function DevModePanel({ onNavigate }: DevModePanelProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { resetDemoVerification } = useDemoMode();
  const { canAccessDevTools } = useTesterAccess();

  // Only show for users with tester access
  if (!canAccessDevTools) {
    return null;
  }

  // Helper to clear all role flags
  const clearAllRoleFlags = () => {
    localStorage.removeItem("dev_admin_mode");
    localStorage.removeItem("dev_clinic_mode");
    localStorage.removeItem("dev_patient_mode");
    localStorage.removeItem("dev_producer_mode");
    localStorage.removeItem("dev_packet_verified_mode");
    localStorage.removeItem("dev_verified_patient_mode");
    localStorage.removeItem("dev_disable_mock");
  };

  const viewStates = [
    {
      icon: Mail,
      label: "Email Sent Screen",
      description: "After signup, waiting for email",
      action: () => {
        // Navigate to signup with email sent state
        window.location.hash = "#/signup?state=emailsent";
      }
    },
    {
      icon: CheckCircle,
      label: "Email Verifying",
      description: "Processing magic link",
      action: () => {
        onNavigate("verify");
      }
    },
    {
      icon: QrCode,
      label: "QR Verification Screen",
      description: "Patient QR code scanning (unverified)",
      action: () => {
        // Reset demo verification to show unverified state
        resetDemoVerification();
        onNavigate("patient-verification");
      }
    },
    {
      icon: UserCheck,
      label: "Patient (Unverified)",
      description: "Logged in, no verification yet",
      action: () => {
        console.log('👤 Dev Mode: Switching to unverified patient...');
        clearAllRoleFlags();
        localStorage.setItem("dev_patient_mode", "true");
        window.location.reload();
      }
    },
    {
      icon: UserCheck,
      label: "Patient (Verified)",
      description: "Clinic QR verification complete",
      action: () => {
        console.log('✅ Dev Mode: Switching to verified patient...');
        clearAllRoleFlags();
        localStorage.setItem("dev_verified_patient_mode", "true");
        window.location.reload();
      }
    },
    {
      icon: CheckCircle,
      label: "Packet Verified",
      description: "Has one approved review",
      action: () => {
        console.log('📦 Dev Mode: Switching to packet verified...');
        clearAllRoleFlags();
        localStorage.setItem("dev_packet_verified_mode", "true");
        window.location.reload();
      }
    },
    {
      icon: Shield,
      label: "Admin",
      description: "Full admin access",
      action: () => {
        console.log('🛡️ Dev Mode: Switching to admin...');
        clearAllRoleFlags();
        localStorage.setItem("dev_admin_mode", "true");
        window.location.reload();
      }
    },
    {
      icon: Building2,
      label: "Clinic Staff",
      description: "CB1 Medical - Clinic Dashboard",
      action: () => {
        console.log('🏥 Dev Mode: Switching to clinic...');
        clearAllRoleFlags();
        localStorage.setItem("dev_clinic_mode", "true");
        window.location.hash = "#/clinic-dashboard";
        window.location.reload();
      }
    },
    {
      icon: Settings,
      label: "Producer",
      description: "Product producer access",
      action: () => {
        console.log('🏭 Dev Mode: Switching to producer...');
        clearAllRoleFlags();
        localStorage.setItem("dev_producer_mode", "true");
        window.location.reload();
      }
    },
    {
      icon: LogOut,
      label: "Guest (Logged Out)",
      description: "View as anonymous user",
      action: async () => {
        console.log('🚪 Dev Mode: Signing out...');
        await supabase.auth.signOut();
        clearAllRoleFlags();
        localStorage.setItem("dev_disable_mock", "true");
        window.location.reload();
      }
    }
  ];

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <Card className="bg-card/95 backdrop-blur-xl border-2 border-primary/20 shadow-2xl overflow-hidden max-w-sm">
        {/* Header */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-accent/50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg gradient-green flex items-center justify-center shadow-lg">
              <Settings className="w-4 h-4 text-white" />
            </div>
            <div className="text-left">
              <p className="text-sm font-medium">Dev Mode Panel</p>
              <p className="text-xs text-muted-foreground">View Auth States</p>
            </div>
          </div>
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-muted-foreground" />
          ) : (
            <ChevronUp className="w-5 h-5 text-muted-foreground" />
          )}
        </button>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="border-t border-border p-4 space-y-2">
            <p className="text-xs text-muted-foreground mb-3">
              Click to view different auth states:
            </p>
            
            {viewStates.map((state, index) => {
              const Icon = state.icon;
              return (
                <Button
                  key={index}
                  variant="outline"
                  className="w-full justify-start gap-3 h-auto py-3"
                  onClick={state.action}
                >
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4 text-primary" />
                  </div>
                  <div className="text-left flex-1">
                    <p className="text-sm font-medium">{state.label}</p>
                    <p className="text-xs text-muted-foreground">
                      {state.description}
                    </p>
                  </div>
                </Button>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}